import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CalendarClock, Play, Pencil, Trash2, Plus, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const CADENCE_LABELS = {
  monthly: "Monthly",
  quarterly: "Quarterly",
  half_yearly: "Half-yearly",
  yearly: "Yearly",
  custom_days: "Every N days",
  custom_weeks: "Every N weeks",
};

const PRIORITIES = ["Urgent", "High", "Medium", "Low"];

const humanCadence = (row) => {
  const label = CADENCE_LABELS[row.cadence] || row.cadence;
  if (row.cadence === "custom_days") return `Every ${row.interval_n} day${row.interval_n === 1 ? "" : "s"}`;
  if (row.cadence === "custom_weeks") return `Every ${row.interval_n} week${row.interval_n === 1 ? "" : "s"}`;
  return label;
};

const fmt = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

export default function ScheduledTicketsPanel() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null); // null | {} for new | doc for edit
  const [accounts, setAccounts] = useState([]);
  const [products, setProducts] = useState([]);
  const [users, setUsers] = useState([]);
  const [busyRowId, setBusyRowId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [r, a, p, u] = await Promise.all([
        api.get("/scheduled-tickets"),
        api.get("/accounts?fields=id,name&limit=1000"),
        api.get("/stax-products?archived=false"),
        api.get("/users"),
      ]);
      setItems(r.data.items || []);
      setStatus(r.data.status || null);
      setAccounts(a.data.items || a.data || []);
      setProducts(p.data.items || p.data || []);
      setUsers((u.data.items || u.data || []).filter((x) => x.user_id));
    } catch (e) {
      toast.error("Could not load scheduled tickets");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing({
    name: "",
    enabled: true,
    account_id: "",
    subject: "",
    description: "",
    priority: "Medium",
    category: "Service",
    product_id: "",
    owner_user_id: "",
    cadence: "monthly",
    interval_n: 1,
    start_date: new Date().toISOString().slice(0, 10),
  });

  const save = async () => {
    if (!editing) return;
    if (!editing.name?.trim()) return toast.error("Name is required");
    if (!editing.account_id) return toast.error("Customer is required");
    if (!editing.subject?.trim()) return toast.error("Subject is required");
    if (!editing.start_date) return toast.error("Start date is required");
    if (["custom_days", "custom_weeks"].includes(editing.cadence) && (!editing.interval_n || editing.interval_n < 1))
      return toast.error("Interval must be ≥ 1");
    const payload = {
      ...editing,
      product_id: editing.product_id || null,
      owner_user_id: editing.owner_user_id || null,
      interval_n: Number(editing.interval_n || 1),
    };
    try {
      if (editing.id) {
        await api.put(`/scheduled-tickets/${editing.id}`, payload);
        toast.success("Schedule updated");
      } else {
        await api.post("/scheduled-tickets", payload);
        toast.success("Schedule created");
      }
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.name}"? Tickets already created will remain.`)) return;
    try {
      await api.delete(`/scheduled-tickets/${row.id}`);
      toast.success("Schedule deleted");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const runNow = async (row) => {
    setBusyRowId(row.id);
    try {
      const r = await api.post(`/scheduled-tickets/${row.id}/run-now`);
      toast.success(`Ticket #${r.data.ticket_number} created`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally { setBusyRowId(null); }
  };

  const toggleEnabled = async (row) => {
    try {
      await api.put(`/scheduled-tickets/${row.id}`, { enabled: !row.enabled });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const nextRunOverdue = (iso) => {
    if (!iso) return false;
    try { return new Date(iso) < new Date(); } catch { return false; }
  };

  return (
    <div className="space-y-4" data-testid="scheduled-tickets-panel">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <CalendarClock className="h-5 w-5 text-blue-600" />
            <h2 className="font-heading text-xl font-semibold text-slate-900">Scheduled tickets</h2>
          </div>
          <p className="text-sm text-slate-500 mt-1 max-w-3xl">
            Recurring tickets for proactive work — monthly SOC reviews, quarterly firewall audits, yearly contract check-ins.
            Each schedule drops a fresh ticket into the queue on its cadence; engineers close it with time logged, creating an auditable trail of the work.
          </p>
          {status?.last_run_at && (
            <p className="text-[11px] text-slate-400 mt-2 font-mono">
              Last scan: {fmt(status.last_run_at)}
              {typeof status.last_created === "number" && ` · ${status.last_created} ticket${status.last_created === 1 ? "" : "s"} created`}
              {status.last_error && <span className="text-rose-600"> · error: {status.last_error}</span>}
            </p>
          )}
        </div>
        <Button onClick={openNew} data-testid="scheduled-new-btn">
          <Plus className="h-4 w-4 mr-1" /> New schedule
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-400">Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <CalendarClock className="h-10 w-10 text-slate-200 mx-auto mb-3" />
              <div className="text-sm text-slate-600 font-medium">No schedules yet</div>
              <div className="text-xs text-slate-400 mt-1 max-w-md mx-auto">
                Create your first schedule to automate recurring tickets — start with the monthly Managed SOC review from your customer contracts.
              </div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-[10px] font-mono uppercase tracking-wider text-slate-500">
                  <th className="px-3 py-2.5">On</th>
                  <th className="px-3 py-2.5">Name / Customer</th>
                  <th className="px-3 py-2.5">Cadence</th>
                  <th className="px-3 py-2.5">Next run</th>
                  <th className="px-3 py-2.5">Last run</th>
                  <th className="px-3 py-2.5">Generated</th>
                  <th className="px-3 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((row) => (
                  <tr key={row.id} className="hover:bg-slate-50" data-testid={`scheduled-row-${row.id}`}>
                    <td className="px-3 py-3">
                      <Switch checked={!!row.enabled} onCheckedChange={() => toggleEnabled(row)} data-testid={`scheduled-enabled-${row.id}`} />
                    </td>
                    <td className="px-3 py-3">
                      <div className="font-medium text-slate-900">{row.name}</div>
                      <div className="text-xs text-slate-500">{row.account_name || "—"} · {row.subject}</div>
                      {row.product_name && (
                        <Badge variant="outline" className="mt-1 text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                          {row.product_name}
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-3 text-slate-700">{humanCadence(row)}</td>
                    <td className="px-3 py-3 font-mono text-xs">
                      <span className={nextRunOverdue(row.next_run_at) && row.enabled ? "text-amber-700 flex items-center gap-1" : "text-slate-600"}>
                        {nextRunOverdue(row.next_run_at) && row.enabled && <AlertTriangle className="h-3 w-3" />}
                        {fmt(row.next_run_at)}
                      </span>
                    </td>
                    <td className="px-3 py-3 font-mono text-xs text-slate-500">{fmt(row.last_run_at)}</td>
                    <td className="px-3 py-3 text-slate-700">{(row.generated_ticket_ids || []).length}</td>
                    <td className="px-3 py-3 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm" variant="outline"
                          onClick={() => runNow(row)}
                          disabled={busyRowId === row.id}
                          data-testid={`scheduled-run-now-${row.id}`}
                          title="Fire this schedule immediately (does not advance next run)"
                        >
                          <Play className="h-3 w-3 mr-1" /> Run now
                        </Button>
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => setEditing(row)}
                          data-testid={`scheduled-edit-${row.id}`}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon" variant="ghost"
                          onClick={() => remove(row)}
                          data-testid={`scheduled-delete-${row.id}`}
                        >
                          <Trash2 className="h-3.5 w-3.5 text-rose-500" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <ScheduledTicketEditor
        open={!!editing}
        value={editing}
        onChange={setEditing}
        onCancel={() => setEditing(null)}
        onSave={save}
        accounts={accounts}
        products={products}
        users={users}
      />
    </div>
  );
}


function ScheduledTicketEditor({ open, value, onChange, onCancel, onSave, accounts, products, users }) {
  if (!open) return null;
  const isEdit = !!value?.id;
  const showInterval = ["custom_days", "custom_weeks"].includes(value?.cadence);
  const set = (k, v) => onChange({ ...value, [k]: v });

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onCancel(); }}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit scheduled ticket" : "New scheduled ticket"}</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Name" required>
              <Input
                value={value.name || ""}
                onChange={(e) => set("name", e.target.value)}
                placeholder="e.g. Monthly Managed SOC review"
                data-testid="scheduled-form-name"
              />
            </Field>
            <Field label="Customer" required>
              <Select value={value.account_id || ""} onValueChange={(v) => set("account_id", v)}>
                <SelectTrigger data-testid="scheduled-form-account"><SelectValue placeholder="Choose customer…" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {accounts.map((a) => <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Subject" required>
            <Input
              value={value.subject || ""}
              onChange={(e) => set("subject", e.target.value)}
              placeholder="e.g. Monthly SOC review — action items & posture check"
              data-testid="scheduled-form-subject"
            />
          </Field>

          <Field label="Description" hint="Shows up as the ticket's initial description">
            <Textarea
              rows={3}
              value={value.description || ""}
              onChange={(e) => set("description", e.target.value)}
              placeholder="Agenda / checklist to complete during the review…"
              data-testid="scheduled-form-description"
            />
          </Field>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Priority">
              <Select value={value.priority || "Medium"} onValueChange={(v) => set("priority", v)}>
                <SelectTrigger data-testid="scheduled-form-priority"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Category">
              <Select value={value.category || "Service"} onValueChange={(v) => set("category", v)}>
                <SelectTrigger data-testid="scheduled-form-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Service">Service</SelectItem>
                  <SelectItem value="Support">Support</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Product" hint="Optional">
              <Select value={value.product_id || "__none__"} onValueChange={(v) => set("product_id", v === "__none__" ? "" : v)}>
                <SelectTrigger data-testid="scheduled-form-product"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="__none__">None</SelectItem>
                  {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </Field>
          </div>

          <Field label="Default owner" hint="Optional — if unset, tickets land unassigned">
            <Select value={value.owner_user_id || "__none__"} onValueChange={(v) => set("owner_user_id", v === "__none__" ? "" : v)}>
              <SelectTrigger data-testid="scheduled-form-owner"><SelectValue placeholder="Unassigned" /></SelectTrigger>
              <SelectContent className="max-h-72">
                <SelectItem value="__none__">Unassigned</SelectItem>
                {users.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>

          <div className="grid sm:grid-cols-3 gap-3">
            <Field label="Cadence" required>
              <Select value={value.cadence || "monthly"} onValueChange={(v) => set("cadence", v)}>
                <SelectTrigger data-testid="scheduled-form-cadence"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(CADENCE_LABELS).map(([k, l]) => (
                    <SelectItem key={k} value={k}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            {showInterval && (
              <Field label={value.cadence === "custom_days" ? "Every N days" : "Every N weeks"} required>
                <Input
                  type="number" min={1}
                  value={value.interval_n || 1}
                  onChange={(e) => set("interval_n", Math.max(1, Number(e.target.value) || 1))}
                  data-testid="scheduled-form-interval"
                />
              </Field>
            )}
            <Field label="Start date" required hint="First ticket drops on this date">
              <Input
                type="date"
                value={value.start_date || ""}
                onChange={(e) => set("start_date", e.target.value)}
                data-testid="scheduled-form-start"
              />
            </Field>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Switch checked={value.enabled ?? true} onCheckedChange={(v) => set("enabled", v)} data-testid="scheduled-form-enabled" />
            <Label className="text-sm text-slate-700">Active — will fire on the cadence above</Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} data-testid="scheduled-form-cancel">Cancel</Button>
          <Button onClick={onSave} data-testid="scheduled-form-save">{isEdit ? "Save changes" : "Create schedule"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, hint, required, children }) {
  return (
    <div className="space-y-1">
      <div className="text-xs text-slate-600 flex items-center gap-1">
        <span>{label}</span>
        {required && <span className="text-rose-500">*</span>}
        {hint && <span className="text-slate-400 font-normal">· {hint}</span>}
      </div>
      {children}
    </div>
  );
}
